import React, { useState, useRef } from "react";
import analytics from "web/script/analytics/analytics";
import strings from "web/script/utils/strings";
import { gettext } from "web/script/modules/django-i18n";
import {
    ProductDescriptionSerializer,
    ProductMoreDescriptionsSerializer,
    ProductSizesSerializer,
} from "web/types/serializers";
import ProductMoreDescriptions from "web/react/components/buybuybuy-area/product-more-descriptions/product-more-descriptions";
import ProductSizes from "web/react/components/buybuybuy-area/product-sizes/product-sizes";
import styles from "./product-description.css";

interface ProductDescriptionProps extends ProductDescriptionSerializer {
    more_descriptions: ProductMoreDescriptionsSerializer;
    product_sizes: ProductSizesSerializer;
}

function ProductDescription({
    title,
    description,
    read_more_limit: readMoreLimit,
    product_sizes: productSizes,
    more_descriptions: moreDescriptions,
}: ProductDescriptionProps): React.ReactElement {
    const analyticsSent = useRef<boolean>(false);

    const [hidden, setHidden] = useState<boolean>(true);

    const words = description?.trim().split(" ");
    let partialText = "";

    if (words) {
        const maxWordCount = readMoreLimit || 20;
        const showWords = 1 + Math.ceil(maxWordCount * 0.8);
        partialText = `${strings.escape(words.slice(0, showWords).join(" "))}&#133;`;
    }

    function handleReadMoreLess(): void {
        if (!analyticsSent.current) {
            analytics.event("Read More Text", "Clicked");
            analytics.event("product_overview", "read_more_details");
            analyticsSent.current = true;
        }

        setHidden((isHidden) => {
            if (!isHidden) {
                // Scroll to the top of the overlay or window
                const overlay = document.querySelector(".base-overlay");

                if (overlay) {
                    overlay.scrollTo({
                        top: 0,
                        left: 0,
                    });
                } else {
                    window.scrollTo({
                        top: 0,
                        left: 0,
                    });
                }
            }

            return !isHidden;
        });
    }

    return (
        <div className={styles.wrapper}>
            <h4 className={styles.title}>{title}</h4>

            <div className={styles.details}>
                {hidden ? (
                    <p dangerouslySetInnerHTML={{ __html: partialText }} />
                ) : (
                    <>
                        <p>{description}</p>

                        {!!moreDescriptions.descriptions.length && (
                            <ProductMoreDescriptions descriptions={moreDescriptions.descriptions} />
                        )}

                        {!!productSizes && <ProductSizes {...productSizes} />}
                    </>
                )}
            </div>

            <div>
                <span className={styles.readMoreToggle} onClick={handleReadMoreLess}>
                    <em>{hidden ? gettext("general.read_more") : gettext("general.read_less")}</em>
                </span>
            </div>
        </div>
    );
}

export default ProductDescription;
